/**
 * Variant Enhancer - Material Intelligence Support Module
 *
 * CCP Phase: Bootstrap (Phase 1)
 * Purpose: Enhances variants with material metadata (wood_gid, finish_gid)
 * Status: FULLY IMPLEMENTED
 *
 * This module is called by the data pipeline Layer 1 to add material intelligence
 * to product variants, enabling Layer 3 to build material relationships.
 *
 * ARCHITECTURE:
 * 1. Initialize with metaobject data (woods, finishes)
 * 2. Build lookup maps for fast material name → GID resolution
 * 3. Parse variant titles to extract material names
 * 4. Match names to metaobjects and generate GIDs
 */

(function () {
  'use strict';

  // Initialize namespace
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  /**
   * SKU to slug mappings for GID generation
   * These convert Shopify SKU codes to metaobject slug format
   */
  const WOOD_SKU_TO_SLUG = {
    WN: 'walnut-veneer',
    MP: 'maple-veneer',
    RO: 'red-oak-veneer',
    KA: 'knotty-alder-veneer',
    'EU-O': 'european-oak',
    PG: 'paint-grade',
    TFL: 'tfl',
    WO: 'white-oak-veneer',
  };

  const FINISH_SKU_TO_SLUG = {
    CHS: 'cherry-stain',
    MS: 'medium-stain',
    DS: 'dark-stain',
    CS: 'clear-stain',
    BS: 'burnt-coal-stain',
    UN: 'unfinished',
    BL: 'black',
    DD: 'dew-drop',
    DG: 'dorian-gray',
    HN: 'hale-navy',
    MN: 'monaco',
    NS: 'natural-stain',
    WW: 'white-washed-white-oak',
    PO: 'primer-only',
    SC: 'suman-clay',
    SS: 'suman-sand',
    WH: 'white',
    WG: 'willow-grey',
  };

  /**
   * Normalize variant image data to avoid runtime fallback chains
   * Pre-computes the final image src and alt during enhancement
   *
   * @param {Object} variant - Raw variant object
   * @returns {Object} - Normalized image with {src, alt}
   */
  const normalizeVariantImage = (variant) => {
    // Handle different image formats (object or string)
    const imageObj = variant.image;
    const featuredImageObj = variant.featured_image;

    // Resolve src with fallback chain
    const src =
      imageObj?.src || // Standard variant.image.src
      imageObj || // String format variant.image
      featuredImageObj?.src || // Featured image object
      featuredImageObj || // String format featured_image
      '/assets/placeholder.jpg'; // Final fallback

    // Resolve alt text with fallback chain
    const alt =
      imageObj?.alt || variant.title || variant.name || 'Product image';

    return { src, alt };
  };

  /**
   * Variant Enhancer Module
   * Provides material intelligence by adding wood_gid and finish_gid to variants
   */
  window.MurphyDoorConfigurator.supportVariantEnhancer = {
    // Lookup maps built during initialization
    woodNameToGid: new Map(),
    finishNameToGid: new Map(),
    woodSlugToSku: new Map(), // Reverse lookup: slug → SKU for O(1) extraction
    finishSlugToSku: new Map(), // Reverse lookup: slug → SKU for O(1) extraction
    initialized: false,
    bootstrapComplete: false, // Phase 1 runtime protection flag

    /**
     * Initialize the variant enhancer with metaobject data
     * MUST be called before enhanceVariant()
     *
     * @param {Array} woodMetaobjects - Array of wood metaobjects {sku, type}
     * @param {Array} finishMetaobjects - Array of finish metaobjects {sku, finish}
     */
    initialize: function (woodMetaobjects, finishMetaobjects) {
      // ============================================================================
      // CRITICAL: BOOTSTRAP-ONLY RUNTIME PROTECTION
      // ============================================================================
      if (this.bootstrapComplete) {
        const error =
          'CRITICAL ARCHITECTURAL VIOLATION: supportVariantEnhancer.initialize() called AFTER bootstrap complete! ' +
          'This is a Phase 1 Bootstrap utility that MUST ONLY be called during data-pipeline.js Layer 1 processing. ' +
          'Runtime Phase 2 modules MUST use pre-enhanced variants from PPD instead.';
        isDev() && console.error(error, new Error().stack);
        throw new Error(error);
      }

      isDev() &&
        console.log(
          '🚀 [VariantEnhancer] Phase 1 execution: Material intelligence processing started'
        );
      isDev() &&
        console.log('🔧 VARIANT ENHANCER: Initializing with metaobject data');

      // Build wood lookup maps: name → GID and slug → SKU
      this.woodNameToGid.clear();
      this.woodSlugToSku.clear();
      if (Array.isArray(woodMetaobjects)) {
        woodMetaobjects.forEach((meta) => {
          if (meta.sku && meta.type && WOOD_SKU_TO_SLUG[meta.sku]) {
            const slug = WOOD_SKU_TO_SLUG[meta.sku];
            const gid = `gid://shopify/Metaobject/${slug}`;
            // Store with normalized key (lowercase, trimmed)
            const normalizedName = meta.type.toLowerCase().trim();
            this.woodNameToGid.set(normalizedName, gid);
            // Build reverse lookup map for O(1) SKU extraction
            this.woodSlugToSku.set(slug, meta.sku);
            isDev() &&
              console.log(
                `  🌳 Wood mapping: "${meta.type}" → ${gid} (SKU: ${meta.sku})`
              );
          }
        });
      }

      // Build finish lookup maps: name → GID and slug → SKU
      this.finishNameToGid.clear();
      this.finishSlugToSku.clear();
      if (Array.isArray(finishMetaobjects)) {
        finishMetaobjects.forEach((meta) => {
          const finishName = meta.finish || meta.type;
          if (meta.sku && finishName && FINISH_SKU_TO_SLUG[meta.sku]) {
            const slug = FINISH_SKU_TO_SLUG[meta.sku];
            const gid = `gid://shopify/Metaobject/${slug}`;
            // Store with normalized key (lowercase, trimmed)
            const normalizedName = finishName.toLowerCase().trim();
            this.finishNameToGid.set(normalizedName, gid);
            // Build reverse lookup map for O(1) SKU extraction
            this.finishSlugToSku.set(slug, meta.sku);
            isDev() &&
              console.log(
                `  🎨 Finish mapping: "${finishName}" → ${gid} (SKU: ${meta.sku})`
              );
          }
        });
      }

      this.initialized = true;
      isDev() &&
        console.log(
          `✅ VARIANT ENHANCER: Initialized with ${this.woodNameToGid.size} woods, ${this.finishNameToGid.size} finishes (O(1) SKU lookups ready)`
        );
    },

    /**
     * Enhance a variant with material intelligence
     * Parses variant title to extract wood/finish names and maps to GIDs
     *
     * @param {Object} variant - The raw Shopify variant
     * @param {Object} productContext - The parent product context; provides featured_image fallback when variant has no own image
     * @returns {Object} - Enhanced variant with wood_gid, finish_gid, wood_sku, finish_sku
     */
    enhanceVariant: function (variant, productContext) {
      // ============================================================================
      // CRITICAL: BOOTSTRAP-ONLY RUNTIME PROTECTION
      // ============================================================================
      if (this.bootstrapComplete) {
        const error =
          'CRITICAL ARCHITECTURAL VIOLATION: supportVariantEnhancer.enhanceVariant() called AFTER bootstrap complete! ' +
          'This is a Phase 1 Bootstrap utility that MUST ONLY be called during data-pipeline.js Layer 1 processing. ' +
          'Runtime Phase 2 modules MUST use pre-enhanced variants from PPD (conductor.getProduct(id).variants) instead.';
        isDev() && console.error(error, new Error().stack);
        throw new Error(error);
      }

      if (!this.initialized) {
        isDev() &&
          console.warn(
            '⚠️ VARIANT ENHANCER: Not initialized! Call initialize() first.'
          );
        return { ...variant };
      }

      const enhanced = { ...variant };

      // Skip "Default Title" variants - they don't have material info
      if (!variant.title || variant.title === 'Default Title') {
        return enhanced;
      }

      // 🚀 OPTIMIZATION: Pre-normalize variant image during enhancement
      // Eliminates runtime fallback chains (100 variants × 2ms = 200ms saved!)
      enhanced.normalizedImage = normalizeVariantImage(variant);

      // 🖼️ FALLBACK: If variant has no own image (featured_image was null in Liquid),
      // use the parent product's featured_image as fallback
      if (
        enhanced.normalizedImage.src === '/assets/placeholder.jpg' &&
        productContext &&
        productContext.featured_image
      ) {
        const prodImg = productContext.featured_image;
        enhanced.normalizedImage = {
          src:
            typeof prodImg === 'string'
              ? prodImg
              : prodImg.src || '/assets/placeholder.jpg',
          alt:
            (typeof prodImg === 'object' && prodImg.alt) ||
            variant.title ||
            'Product image',
        };
      }

      // Parse variant title: "Wood Name / Finish Name"
      const parts = variant.title.split('/').map((s) => s.trim());

      if (parts.length >= 2) {
        const woodName = parts[0];
        const finishName = parts[1];

        // Look up wood GID
        const woodGid = this.lookupWoodGid(woodName);
        if (woodGid) {
          enhanced.wood_gid = woodGid;
          enhanced.wood_sku = this.extractSkuFromGid(woodGid, true);
        }

        // Look up finish GID
        const finishGid = this.lookupFinishGid(finishName);
        if (finishGid) {
          enhanced.finish_gid = finishGid;
          enhanced.finish_sku = this.extractSkuFromGid(finishGid, false);
        }
      } else {
        // Handle single-part titles (might be wood-only or finish-only)
        const singleName = parts[0];

        // Try wood first
        const woodGid = this.lookupWoodGid(singleName);
        if (woodGid) {
          enhanced.wood_gid = woodGid;
          enhanced.wood_sku = this.extractSkuFromGid(woodGid, true);
        } else {
          // Try finish
          const finishGid = this.lookupFinishGid(singleName);
          if (finishGid) {
            enhanced.finish_gid = finishGid;
            enhanced.finish_sku = this.extractSkuFromGid(finishGid, false);
          }
        }
      }

      return enhanced;
    },

    /**
     * Look up wood GID by name (case-insensitive)
     * @param {string} woodName - The wood name to look up
     * @returns {string|null} - Wood GID or null
     */
    lookupWoodGid: function (woodName) {
      if (!woodName) return null;
      const normalized = woodName.toLowerCase().trim();
      return this.woodNameToGid.get(normalized) || null;
    },

    /**
     * Look up finish GID by name (case-insensitive)
     * @param {string} finishName - The finish name to look up
     * @returns {string|null} - Finish GID or null
     */
    lookupFinishGid: function (finishName) {
      if (!finishName) return null;
      const normalized = finishName.toLowerCase().trim();
      return this.finishNameToGid.get(normalized) || null;
    },

    /**
     * Extract SKU code from GID using O(1) reverse lookup
     * @param {string} gid - GID like "gid://shopify/Metaobject/walnut-veneer"
     * @param {boolean} isWood - True for wood lookup, false for finish lookup (required)
     * @returns {string|null} - SKU code or null
     */
    extractSkuFromGid: function (gid, isWood) {
      if (!gid || typeof isWood === 'undefined') return null;
      // Extract slug from GID (last part after final /)
      const slug = gid.split('/').pop();
      // O(1) reverse lookup from pre-built map
      const lookupMap = isWood ? this.woodSlugToSku : this.finishSlugToSku;
      return lookupMap.get(slug) || null;
    },

    /**
     * Marks bootstrap as complete - enables runtime protection
     * Called by data-pipeline after pipeline completes
     */
    markBootstrapComplete: function () {
      this.bootstrapComplete = true;
      isDev() &&
        console.log(
          '🎯 VariantEnhancer: Bootstrap complete - runtime protection enabled'
        );
    },
  };
})();
